import {
  CREATE_COMMENT_REACTION,
  CommentReactionId,
  DELETE_COMMENT_REACTION,
} from "@hex/common";
import type { BaseEmoji, EmojiData } from "emoji-mart";
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import { v4 as uuid } from "uuid";

import { HexButton, HexPopover, HexTooltip } from "../../hex-components";
import { CommentModelFragment } from "../../hex-version-multiplayer/handlers/commentClientHandlers.generated";
import { useCurrentUser } from "../../hooks/me/useCurrentUser";
import { useToggleState } from "../../hooks/useToggleState";
import { useHexVersionAOContext } from "../../util/hexVersionAOContext";
import { EmojiPicker } from "../common/emoji/EmojiPicker";
import { AddReactionIcon } from "../icons/CustomIcons";

const StyledHexPopover = styled(HexPopover)`
  && {
    display: flex;
  }
`;

const StyledHexTooltip = styled(HexTooltip)`
  && {
    display: flex;
  }
`;

type Reaction = CommentModelFragment["reactions"][0];

export interface CommentReactionButtonProps {
  readonly commentId: string;
  readonly onToggle?: (isOpen: boolean) => void;
  readonly reactions: readonly Reaction[];
}

export const CommentReactionButton: React.ComponentType<CommentReactionButtonProps> =
  React.memo(function CommentReactionButton({
    commentId,
    onToggle,
    reactions,
  }: CommentReactionButtonProps) {
    const currentUser = useCurrentUser();
    const [isOpen, _, { setFalse: onClose, setTrue: onOpen }] =
      useToggleState(false);
    const { dispatchAO } = useHexVersionAOContext();

    const currentUserReactionsByContent = useMemo(() => {
      const reactionsByContent: Record<string, Reaction> = {};
      if (currentUser == null) {
        return reactionsByContent;
      }

      reactions.forEach((r) => {
        if (r.creatorId === currentUser.id) {
          reactionsByContent[r.content] = r;
        }
      });
      return reactionsByContent;
    }, [currentUser, reactions]);

    const onSelectEmoji = useCallback(
      (emoji: EmojiData) => {
        if (currentUser != null) {
          const content = (emoji as BaseEmoji).native;
          if (currentUserReactionsByContent[content] != null) {
            dispatchAO(
              DELETE_COMMENT_REACTION.create({
                commentId,
                commentReactionId: currentUserReactionsByContent[content].id,
                content,
                creatorId: currentUser.id,
              }),
            );
          } else {
            dispatchAO(
              CREATE_COMMENT_REACTION.create({
                commentId,
                commentReactionId: uuid() as CommentReactionId,
                content,
                creatorNameOrEmail: currentUser.name ?? currentUser.email,
                creatorId: currentUser.id,
              }),
            );
          }
        }
        onClose();
      },
      [
        commentId,
        dispatchAO,
        onClose,
        currentUser,
        currentUserReactionsByContent,
      ],
    );

    if (currentUser == null) {
      return null;
    }

    return (
      <StyledHexPopover
        content={<EmojiPicker onSelect={onSelectEmoji} />}
        isOpen={isOpen}
        onClose={onClose}
        onInteraction={onToggle}
      >
        <StyledHexTooltip content="Add reaction" placement="bottom">
          <HexButton $extraSmall={true} minimal={true} onClick={onOpen}>
            <AddReactionIcon />
          </HexButton>
        </StyledHexTooltip>
      </StyledHexPopover>
    );
  });
